import { useEffect, useState } from 'react'
import Markdown from 'react-markdown'
import { theme } from '../theme'
import { api } from '../api'
import { navigate } from '../router'

// AgentDocViewer renders an embedded agent-doc as Markdown at the SPA route
// `/agent-docs/<path>`. An empty path shows the index. The docs are the same
// reference library the chat agent reads via the docs_search / docs_get tools.
export function AgentDocViewer({ path }: { path: string }) {
  if (path === '') return <AgentDocIndex />
  return <AgentDocPage path={path} />
}

type DocEntry = { path: string; description: string }

function AgentDocIndex() {
  const [docs, setDocs] = useState<DocEntry[] | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    api.agentDocs().then(setDocs).catch(e => setErr(String(e)))
  }, [])

  // Partition the flat index into the three kinds of thing it actually holds.
  // None of these are files on disk — the prose docs are rendered from embedded
  // sources, and the system prompt / tool schemas are generated from the
  // running app — so the slugs are extension-less and we group them by kind.
  const tools = docs?.filter(d => d.path.startsWith('tools/')) ?? []
  const system = docs?.filter(d => d.path === 'system-prompt') ?? []
  const reference = docs?.filter(d => !d.path.startsWith('tools/') && d.path !== 'system-prompt') ?? []

  return (
    <Frame title="Agent reference library">
      {err && <div style={{ color: theme.accentInk }}>{err}</div>}
      {!docs && !err && <div style={{ color: theme.inkSoft }}>Loading…</div>}
      {docs && (
        <>
          <DocSection
            title="Reference docs"
            blurb="Domain rules and conventions the agent consults via docs_search / docs_get."
            entries={reference}
          />
          <DocSection
            title="Tool schemas"
            blurb="The JSON schema for each tool the agent can call. Generated live from the running app — not files."
            entries={tools}
          />
          <DocSection
            title="System prompt"
            blurb="The instructions the agent receives at the start of every conversation."
            entries={system}
          />
        </>
      )}
    </Frame>
  )
}

function DocSection({ title, blurb, entries }: {
  title: string; blurb: string; entries: DocEntry[]
}) {
  if (entries.length === 0) return null
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{
        fontFamily: theme.serif, fontWeight: theme.headWeight,
        fontSize: 20, margin: '0 0 4px',
      }}>{title}</h2>
      <div style={{ color: theme.inkSoft, fontSize: 13, marginBottom: 12 }}>{blurb}</div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {entries.map(d => (
          <li key={d.path} style={{ marginBottom: 12 }}>
            <NavLink href={`/agent-docs/${d.path}`}><code>{d.path}</code></NavLink>
            {d.description && (
              <div style={{ color: theme.inkSoft, fontSize: 14, marginTop: 2 }}>
                {d.description}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

function AgentDocPage({ path }: { path: string }) {
  const [body, setBody] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setBody(null); setErr(null)
    api.agentDoc(path)
      .then(d => setBody(d.body))
      .catch(e => setErr(String(e)))
  }, [path])

  return (
    <Frame title={path} crumb={{ label: '← All agent docs', href: '/agent-docs' }}>
      {err && <div style={{ color: theme.accentInk }}>{err}</div>}
      {!body && !err && <div style={{ color: theme.inkSoft }}>Loading…</div>}
      {body && (
        <div style={{
          fontFamily: theme.sans, fontSize: 15, lineHeight: 1.6, color: theme.ink,
        }} className="agent-doc-body">
          <Markdown>{body}</Markdown>
        </div>
      )}
    </Frame>
  )
}

function Frame({
  title, crumb, children,
}: { title: string; crumb?: { label: string; href: string }; children: React.ReactNode }) {
  return (
    <div style={{
      maxWidth: 760, margin: '0 auto', padding: '32px 24px 80px',
      fontFamily: theme.sans, color: theme.ink,
    }}>
      <NavLink href="/plan">← Back to Mila</NavLink>
      {crumb && (
        <div style={{ marginTop: 4 }}>
          <NavLink href={crumb.href}>{crumb.label}</NavLink>
        </div>
      )}
      <div style={{
        marginTop: 20, padding: '12px 14px',
        border: `1px solid ${theme.line}`, borderRadius: theme.radius,
        background: theme.surface, color: theme.inkSoft,
        fontSize: 13, lineHeight: 1.55,
      }}>
        These docs are written for the Mila AI agent. It is the primary
        audience. We expose them to end users out of respect for the curiosity
        of the more technically inclined. These docs are not intended as a
        guide for how humans should use the app! Everything here is a live view
        generated from the running app — the prose reference, the system prompt,
        and each tool's schema — not a set of files on disk.
      </div>
      <h1 style={{
        fontFamily: theme.serif, fontWeight: theme.headWeight,
        fontSize: 32, margin: '16px 0 24px',
      }}>{title}</h1>
      <style>{`
        .agent-doc-body h1 { font-family: ${theme.serif}; font-weight: ${theme.headWeight}; font-size: 28px; margin: 32px 0 12px; }
        .agent-doc-body h2 { font-family: ${theme.serif}; font-weight: ${theme.headWeight}; font-size: 22px; margin: 28px 0 10px; }
        .agent-doc-body h3 { font-family: ${theme.serif}; font-weight: ${theme.headWeight}; font-size: 18px; margin: 24px 0 8px; }
        .agent-doc-body p { margin: 0 0 12px; }
        .agent-doc-body ul, .agent-doc-body ol { margin: 0 0 12px; padding-left: 24px; }
        .agent-doc-body li { margin-bottom: 4px; }
        .agent-doc-body code { font-family: ${theme.mono}; font-size: 13.5px; background: ${theme.surface2}; padding: 1px 5px; border-radius: 3px; }
        .agent-doc-body pre { font-family: ${theme.mono}; font-size: 13px; background: ${theme.surface2}; padding: 12px 14px; border-radius: ${theme.radius}px; overflow-x: auto; margin: 0 0 14px; border: 1px solid ${theme.line}; }
        .agent-doc-body pre code { background: transparent; padding: 0; }
        .agent-doc-body blockquote { border-left: 3px solid ${theme.line}; padding-left: 12px; margin: 0 0 14px; color: ${theme.inkSoft}; }
        .agent-doc-body a { color: ${theme.accent}; }
        .agent-doc-body table { border-collapse: collapse; margin: 0 0 14px; }
        .agent-doc-body th, .agent-doc-body td { border: 1px solid ${theme.line}; padding: 6px 10px; text-align: left; }
      `}</style>
      {children}
    </div>
  )
}

// NavLink navigates client-side but keeps a real href so middle-click /
// copy-link / right-click still work.
export function NavLink({ href, children, style }: {
  href: string; children: React.ReactNode; style?: React.CSSProperties
}) {
  return (
    <a
      href={href}
      onClick={e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
        e.preventDefault()
        navigate(href)
      }}
      style={{ color: theme.accent, textDecoration: 'none', ...style }}
    >{children}</a>
  )
}
